const SWIPE_CLOSE_THRESHOLD = 90;
const SWIPE_CLOSE_VELOCITY = 0.45;

/**
 * @returns {boolean}
 */
function isTouchDevice() {
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

/**
 * @param {HTMLElement} gallery
 * @returns {number}
 */
function getActiveSlideIndex(gallery) {
  const slides = gallery.querySelectorAll(':scope > li');
  if (!slides.length) return 0;

  const scrollLeft = gallery.scrollLeft;
  const width = gallery.clientWidth || 1;
  return Math.round(scrollLeft / width);
}

/**
 * @param {zoom-dialog} zoomDialog
 * @param {number} index
 */
function updateMobileUI(zoomDialog, index) {
  const ui = zoomDialog.querySelector('.stryxx-zoom-mobile-ui');
  if (!ui) return;

  const current = ui.querySelector('[data-stryxx-zoom-current]');
  if (current) current.textContent = String(index + 1);

  ui.querySelectorAll('.stryxx-zoom-mobile-ui__dot').forEach((dot, i) => {
    dot.classList.toggle('is-active', i === index);
  });
}

/**
 * @param {zoom-dialog} zoomDialog
 */
function initSwipeDownToClose(zoomDialog) {
  const dialog = zoomDialog.querySelector('dialog');
  const gallery = zoomDialog.querySelector('.dialog-zoomed-gallery');
  if (!dialog || !gallery) return;

  let startY = 0;
  let startX = 0;
  let startTime = 0;
  let currentY = 0;
  let tracking = false;
  let isZoomed = false;

  const resetDialogStyle = () => {
    dialog.style.transform = '';
    dialog.style.opacity = '';
    dialog.style.transition = '';
  };

  zoomDialog.addEventListener('stryxx:zoom-change', (event) => {
    if (!(event instanceof CustomEvent)) return;
    isZoomed = Boolean(event.detail?.zoomed);
    gallery.classList.toggle('stryxx-zoom-gallery--zoomed', isZoomed);
  });

  dialog.addEventListener(
    'touchstart',
    (event) => {
      if (!isTouchDevice() || isZoomed || event.touches.length !== 1) return;

      const target = event.target;
      if (target instanceof Element && target.closest('drag-zoom-wrapper, button, .dialog-thumbnails-list')) {
        return;
      }

      startY = event.touches[0].clientY;
      startX = event.touches[0].clientX;
      startTime = event.timeStamp;
      currentY = startY;
      tracking = true;
      dialog.style.transition = 'none';
    },
    { passive: true }
  );

  dialog.addEventListener(
    'touchmove',
    (event) => {
      if (!tracking || isZoomed) return;

      const touch = event.touches[0];
      const dy = touch.clientY - startY;
      const dx = touch.clientX - startX;

      if (dy <= 0 || Math.abs(dx) > Math.abs(dy)) return;

      currentY = touch.clientY;
      const progress = Math.min(dy / 320, 1);
      dialog.style.transform = `translateY(${dy * 0.55}px) scale(${1 - progress * 0.04})`;
      dialog.style.opacity = String(1 - progress * 0.55);
    },
    { passive: true }
  );

  dialog.addEventListener('touchend', (event) => {
    if (!tracking) return;
    tracking = false;

    const dy = currentY - startY;
    const duration = event.timeStamp - startTime;
    const velocity = dy / Math.max(duration, 1);

    if (dy > SWIPE_CLOSE_THRESHOLD || velocity > SWIPE_CLOSE_VELOCITY) {
      dialog.style.transition = 'transform 0.28s ease, opacity 0.28s ease';
      dialog.style.transform = 'translateY(100%) scale(0.96)';
      dialog.style.opacity = '0';
      window.setTimeout(() => {
        /** @type {{ close?: () => void }} */ (zoomDialog).close?.();
        resetDialogStyle();
      }, 260);
      return;
    }

    dialog.style.transition = 'transform 0.32s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.32s ease';
    resetDialogStyle();
  });

  zoomDialog.addEventListener('stryxx:zoom-dialog-close', resetDialogStyle);
}

/**
 * @param {zoom-dialog} zoomDialog
 */
function initGalleryScrollSync(zoomDialog) {
  const gallery = zoomDialog.querySelector('.dialog-zoomed-gallery');
  if (!gallery) return;

  let scrollTimeout = 0;
  let lastIndex = getActiveSlideIndex(gallery);
  let isZoomed = false;

  zoomDialog.addEventListener('stryxx:zoom-change', (event) => {
    if (!(event instanceof CustomEvent)) return;
    isZoomed = Boolean(event.detail?.zoomed);
  });

  gallery.addEventListener(
    'scroll',
    () => {
      if (isZoomed) return;

      window.clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(() => {
        const index = getActiveSlideIndex(gallery);
        if (index === lastIndex) return;

        lastIndex = index;
        updateMobileUI(zoomDialog, index);
        window.dispatchEvent(
          new CustomEvent('stryxx:zoom-slide-change', {
            bubbles: true,
            detail: { index },
          })
        );
      }, 80);
    },
    { passive: true }
  );
}

/**
 * @param {media-gallery} mediaGallery
 */
function initTapToOpen(mediaGallery) {
  const zoomDialog = mediaGallery.querySelector('zoom-dialog');
  if (!zoomDialog) return;

  let startX = 0;
  let startY = 0;
  let moved = false;

  mediaGallery.addEventListener(
    'touchstart',
    (event) => {
      if (!isTouchDevice()) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      moved = false;
    },
    { passive: true }
  );

  mediaGallery.addEventListener(
    'touchmove',
    (event) => {
      if (!isTouchDevice()) return;
      const dx = Math.abs(event.touches[0].clientX - startX);
      const dy = Math.abs(event.touches[0].clientY - startY);
      if (dx > 12 || dy > 12) moved = true;
    },
    { passive: true }
  );

  mediaGallery.addEventListener('touchend', (event) => {
    if (!isTouchDevice() || moved) return;

    const dialog = zoomDialog.querySelector('dialog');
    if (dialog?.open) return;

    const zoomable = event.target instanceof Element ? event.target.closest('.product-media-container--zoomable') : null;
    if (!zoomable) return;

    const slide = zoomable.closest('slideshow-slide');
    if (!slide) return;

    const slides = [...mediaGallery.querySelectorAll('slideshow-slide')];
    const index = slides.indexOf(slide);
    if (index < 0) return;

    /** @type {{ open?: (index: number, event: Event) => void }} */ (zoomDialog).open?.(index, event);
  });
}

/**
 * @param {zoom-dialog} zoomDialog
 */
function initZoomDialog(zoomDialog) {
  const ui = zoomDialog.querySelector('.stryxx-zoom-mobile-ui');
  const dialog = zoomDialog.querySelector('dialog');

  zoomDialog.addEventListener('stryxx:zoom-dialog-open', (event) => {
    if (!(event instanceof CustomEvent)) return;
    if (ui && isTouchDevice()) {
      ui.hidden = false;
      updateMobileUI(zoomDialog, event.detail?.index ?? 0);
    }
    dialog?.classList.add('stryxx-zoom-dialog--open');
  });

  zoomDialog.addEventListener('stryxx:zoom-dialog-close', () => {
    if (ui) ui.hidden = true;
    dialog?.classList.remove('stryxx-zoom-dialog--open');
  });

  initSwipeDownToClose(zoomDialog);
  initGalleryScrollSync(zoomDialog);

  const gallery = zoomDialog.querySelector('.dialog-zoomed-gallery');
  gallery?.querySelectorAll('li.product-media-container--image').forEach((slide) => {
    slide.addEventListener(
      'click',
      (event) => {
        if (!isTouchDevice()) return;
        event.stopPropagation();
        event.preventDefault();
      },
      true
    );
  });
}

function init() {
  if (!isTouchDevice()) return;

  document.querySelectorAll('zoom-dialog').forEach((zoomDialog) => {
    initZoomDialog(zoomDialog);
  });

  document.querySelectorAll('media-gallery').forEach((mediaGallery) => {
    initTapToOpen(mediaGallery);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
